<!-- Used to change the timezone according to your organization's location
In (UI) System Setting-> Timezone
-->
<template>
  <div>
    <v-row no-gutters>
      <v-col sm="8" class="pa-0">
        <h2>Choose Timezone</h2>
      </v-col>
    </v-row>
    <v-row v-if="!initialLoading">
      <v-col class="mx-0 mt-1">
        <label>Set the timezone for your organization</label>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="mx-0 mt-1">
        <v-skeleton-loader
          type="heading"
          :loading="true"
          min-width="100%"
        ></v-skeleton-loader>
      </v-col>
    </v-row>
    <v-row v-if="!initialLoading" no-gutters>
      <v-col sm="5" class="mx-0 mt-1">
        <span>Choose timezone:</span>
      </v-col>
      <v-col class="positionRelative" sm="7" v-if="!initialLoading" :data-test-id="`${dataTestId}__timezone-select-box-wrapper`">
        <v-autocomplete
          ref="timezoneAutocomplete"
          name="timezoneData"
          label="Select Timezone"
          solo
          flat
          height="52px"
          hide-selected
          type
          v-model="selectedSource"
          background-color="#E5E5E5 !important"
          :items="timezones"
          item-text="label"
          item-value="id"
          append-icon="keyboard_arrow_down"
          :attach="`[data-test-id='${dataTestId}__timezone-select-box-wrapper']`"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{ data.item.label }}</v-list-item-title>
              <v-list-item-subtitle>{{
                data.item.offset
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col sm="5" class="mx-0 mt-1">
        <v-skeleton-loader
          type="heading"
          :loading="true"
          min-width="100%"
        ></v-skeleton-loader>
      </v-col>
      <v-col sm="7" class="mx-0 mt-1">
        <v-skeleton-loader
          type="button"
          :loading="true"
          min-width="100%"
        ></v-skeleton-loader>
      </v-col>
    </v-row>
    <v-row justify="end" v-if="!initialLoading">
      <v-col class="my-8 text-right">
        <x-button
          outline
          color="#58e2c2"
          @click="getTimezone"
          buttonText="Reset"
          :data-test-id="`${dataTestId}__reset-btn`"
        />
        <x-button
          color="#58e2c2"
          @click="openConfirmBox"
          buttonText="Save Settings"
          class="ml-2"
          :data-test-id="`${dataTestId}__save-settings-btn`"
        />
      </v-col>
    </v-row>
    <x-dialog
      :dialogState="confirmDialogueVar"
      :dialogTitle="dialogTitle"
      :dialogText="dialogText"
      :confirmDialog="true"
      :actions="{
        show: true,
        secondaryButtonFunction: cancelSaveSetting,
        primaryButtonFunction: changeTimezone,
        primaryButtonShow: true,
        secondaryButtonText: cancelText,
        primaryButtonText: successText
      }"
      @toggleModal="cancelSaveSetting"
    ></x-dialog>
  </div>
</template>

<script>
import PreferencesService from '@/services/PreferencesService';
import PreferenceHelper from '@/helpers/PreferenceHelper';
import { mapMutations } from 'vuex';

export default {
  data() {
    return {
      timezones: [],
      selectedSource: 'Zulu',
      timezoneId: '',
      confirmDialogueVar: false,
      dialogTitle: '',
      dialogText: '',
      cancelText: '',
      successText: '',
      preferenceServiceHelper: null,
      initialLoading: false,
      dataTestId:'configuration__timezone'
    };
  },
  props: ['title'],
  methods: {
    ...mapMutations(['setCurrentTimeZone']),
    // to get the time zone list
    async getTimeZoneList() {
      this.initialLoading = true;
      try {
        let response = await PreferencesService.getPreferencesByPreferenceKey(
          this.$preference.TIMEZONES
        );
        let parsedResponse = this.parseResponse(response);
        if (parsedResponse !== false) {
          let self = this;
          parsedResponse[0].results[0].data.list.zones.forEach(function(
            timezone
          ) {
            timezone = timezone.split('|');
            self.timezones.push({
              id: timezone[0].split('-')[0],
              offset: timezone[1],
              label: `${timezone[0]}`
            });
          });
        } else {
          this.snackbar('error', this.$message.CANNOT_LOAD_SETTING_DATA);
        }
        this.initialLoading = false;
      } catch (err) {
        this.snackbar('error', err);
        this.initialLoading = false;
      }
    },
    // to get the saved time zone after reset the timezone settings
    async getTimezone() {
      try {
        let response = await PreferencesService.getPreferencesByPreferenceKey(
          this.$preference.TIMEZONE
        );
        let parsedResponse = this.parseResponse(response);

        if (parsedResponse !== false) {
          const timezoneId = this.timezones.findIndex(
            timezone =>
              parsedResponse[0].results[0].data.timezone === timezone.offset
          );
          this.selectedSource = this.timezones[timezoneId].id;

          this.timezoneId = parsedResponse[0].results[0].id;
        } else {
          this.snackbar('error', this.$message.CANNOT_LOAD_SETTING_DATA);
        }
      } catch (err) {
        this.snackbar('error', err);
      }
    },
    // to set confirm box dialog data to save timezone
    openConfirmBox() {
      this.dialogTitle = 'Please Confirm';
      this.dialogText = 'Are you sure you want to save timezone?';
      this.cancelText = 'Cancel';
      this.successText = 'Yes';
      this.confirmDialogueVar = true;
    },
    // to update the saved timezone
    async changeTimezone() {
      const timezoneId = this.timezones.findIndex(
        timezone => this.selectedSource === timezone.id
      );
      let reqObject = this.preferenceServiceHelper.createPreferenceBody(
        'timezone',
        { timezone: this.timezones[timezoneId].offset }
      );
      let url = '';
      if (
        this.timezoneId === this.preferenceServiceHelper.defaultPreferenceId
      ) {
        url = PreferencesService.addPreference(
          this.$preference.TIMEZONE,
          reqObject
        );
      } else {
        url = PreferencesService.updatePreference(
          this.$preference.TIMEZONE,
          this.timezoneId,
          reqObject
        );
      }
      try {
        let res = await url;
        let parsedResponse = this.parseResponse(res);
        if (parsedResponse !== false) {
          this.timezoneId = parsedResponse[0].id;

          this.selectedSource = this.timezones[timezoneId].id;
          this.setCurrentTimeZone({
            source: this.timezones[timezoneId].offset,
            timezoneId: this.timezoneId
          });
          this.snackbar('success', this.$message.SAVED_SUCCESS);
        } else {
          this.snackbar('error', this.$message.CANNOT_LOAD_SETTING_DATA);
        }
        this.confirmDialogueVar = false;
      } catch (err) {
        this.snackbar('error', err);
      }
    },
    async getOrgTimeZoneData() {
      await this.getTimeZoneList();
      await this.getTimezone();
    },
    // to close confirm dialog box without changing the timezone
    cancelSaveSetting() {
      this.confirmDialogueVar = false;
    }
  },
  mounted() {
    this.preferenceServiceHelper = new PreferenceHelper();
    this.getOrgTimeZoneData();
  },
  watch: {
    currentlySelectedOrgId: function() {
      this.getOrgTimeZoneData();
    }
  }
};
</script>

<style>
.v-skeleton-loader__button {
  width: 100% !important;
  height: 50px;
}
</style>
